import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Plus, Building, Building2, Trash2, ChevronRight, ChevronDown, Globe, Store,
  DownloadCloud, Loader2, CalendarRange, Users,
} from 'lucide-react';

function AddCompanyDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim() || !subdomain.trim()) { toast.error('Vyplňte názov aj subdoménu.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/companies', { name: name.trim(), subdomain: subdomain.trim() });
      toast.success(`Klient „${res.data.name}" vytvorený`);
      setName(''); setSubdomain(''); setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Vytvorenie klienta zlyhalo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-company-btn" className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Pridať klienta
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="add-company-dialog">
        <DialogHeader>
          <DialogTitle>Nový klient</DialogTitle>
          <DialogDescription>
            Subdoména musí presne sedieť s tým, čo TiDo posiela v odkaze (napr. <span className="font-mono">cooppp</span> z <span className="font-mono">cooppp.tido.sk</span>).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500">Názov klienta</Label>
            <Input data-testid="company-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="napr. COOP Poprad" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Subdoména</Label>
            <div className="flex items-center gap-1 mt-1">
              <Input data-testid="company-subdomain-input" value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="cooppp" className="font-mono" />
              <span className="text-sm text-slate-400 whitespace-nowrap">.tido.sk</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="company-save-btn" onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ukladám…</> : 'Vytvoriť klienta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportStoreForm({ company, onDone }) {
  const [centerId, setCenterId] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [importing, setImporting] = useState(false);

  const run = async () => {
    if (!centerId) { toast.error('Zadajte IDP (ID strediska v TiDo).'); return; }
    const [rok, mesiac] = month.split('-').map(Number);
    setImporting(true);
    try {
      const res = await api.post('/otido/import', {
        company_id: company.id, center_id: Number(centerId), rok, mesiac,
      });
      const d = res.data;
      toast.success(`${d.updated ? 'Aktualizované' : 'Importované'}: ${d.name} (${d.employees_imported} zam., ${d.shifts} zmien)`);
      setCenterId('');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import z TiDo zlyhal');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card className="p-3 border-emerald-200 bg-emerald-50/50">
      <div className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5 mb-2">
        <DownloadCloud className="w-3.5 h-3.5" /> Importovať stredisko z TiDo
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid={`import-idp-${company.id}`} type="number" placeholder="IDP" value={centerId}
          onChange={(e) => setCenterId(e.target.value)} className="h-8 text-sm w-28 bg-white" />
        <Input data-testid={`import-month-${company.id}`} type="month" value={month}
          onChange={(e) => setMonth(e.target.value)} className="h-8 text-sm w-36 bg-white" />
        <Button data-testid={`import-run-${company.id}`} size="sm" disabled={importing} onClick={run}
          className="bg-emerald-700 hover:bg-emerald-800 text-white h-8">
          {importing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importujem…</> : 'Načítať / synchronizovať'}
        </Button>
      </div>
      <p className="text-[11px] text-emerald-700/70 leading-tight mt-2">
        Stiahne otváracie hodiny, počty na zmenách a zamestnancov. Existujúce stredisko sa aktualizuje (nezduplikuje).
      </p>
    </Card>
  );
}

function CompanyRow({ company, stores, expanded, onToggle, onDeleteCompany, onDeleteStore, onImportDone, onPlan }) {
  return (
    <Card data-testid={`company-${company.id}`} className="overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
        <button data-testid={`company-toggle-${company.id}`} onClick={onToggle}
          className="flex items-center gap-3 flex-1 text-left">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
            <Building className="w-4.5 h-4.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 truncate">{company.name}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1">
              <Globe className="w-3 h-3" /> <span className="font-mono">{company.subdomain}.tido.sk</span>
            </div>
          </div>
          <Badge variant="secondary" className="mr-1">
            <Store className="w-3 h-3 mr-1" /> {company.store_count ?? stores.length} {(company.store_count ?? stores.length) === 1 ? 'stredisko' : 'stredísk'}
          </Badge>
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid={`delete-company-${company.id}`} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Zmazať klienta „{company.name}"?</AlertDialogTitle>
              <AlertDialogDescription>
                Zmažú sa aj VŠETKY jeho strediská ({company.store_count ?? stores.length}) vrátane zamestnancov a rozvrhov. Túto akciu nie je možné vrátiť späť.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Zrušiť</AlertDialogCancel>
              <AlertDialogAction data-testid={`confirm-delete-company-${company.id}`}
                onClick={() => onDeleteCompany(company)} className="bg-red-600 hover:bg-red-700">
                Zmazať klienta
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50">
          <ImportStoreForm company={company} onDone={onImportDone} />

          {stores.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-3">
              Zatiaľ žiadne stredisko. Importujte prvé stredisko cez IDP vyššie.
            </div>
          ) : (
            <div className="space-y-2">
              {stores.map((s) => (
                <div key={s.id} data-testid={`store-${s.id}`}
                  className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-3 py-2.5">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-500">
                    <Store className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {s.employee_count ?? '—'} zam.</span>
                      {s.tido_group_id && <span>skupina {s.tido_group_id}</span>}
                    </div>
                  </div>
                  <Button data-testid={`plan-store-${s.id}`} size="sm" variant="outline"
                    onClick={() => onPlan(s.id)} className="border-slate-300">
                    <CalendarRange className="w-4 h-4 mr-1.5" /> Plánovať
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-store-${s.id}`} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Zmazať stredisko „{s.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Zmažú sa aj jeho zamestnanci a rozvrhy. Túto akciu nie je možné vrátiť späť.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Zrušiť</AlertDialogCancel>
                        <AlertDialogAction data-testid={`confirm-delete-store-${s.id}`}
                          onClick={() => onDeleteStore(s)} className="bg-red-600 hover:bg-red-700">
                          Zmazať stredisko
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function KlientiPage({ workplaces, refreshWorkplaces, onPlan }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (e) {
      toast.error('Nepodarilo sa načítať klientov');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const storesFor = (cid) => workplaces.filter((w) => w.company_id === cid);

  const deleteCompany = async (company) => {
    try {
      await api.delete(`/companies/${company.id}`);
      toast.success(`Klient „${company.name}" zmazaný`);
      await Promise.all([loadCompanies(), refreshWorkplaces()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mazanie klienta zlyhalo');
    }
  };

  const deleteStore = async (store) => {
    try {
      await api.delete(`/workplaces/${store.id}`);
      toast.success(`Stredisko „${store.name}" zmazané`);
      await Promise.all([loadCompanies(), refreshWorkplaces()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mazanie strediska zlyhalo');
    }
  };

  const afterImport = async () => {
    await Promise.all([loadCompanies(), refreshWorkplaces()]);
  };

  return (
    <div data-testid="klienti-page" className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Klienti
          </h2>
          <p className="text-sm text-slate-400">Správa klientov (firiem) a ich stredísk. Klient musí existovať skôr, než klikne na svoj TiDo odkaz.</p>
        </div>
        <AddCompanyDialog onCreated={loadCompanies} />
      </div>

      {loading ? (
        <Card className="p-8 text-center text-slate-400">Načítavam klientov…</Card>
      ) : companies.length === 0 ? (
        <Card className="p-10 text-center">
          <Building className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-600 font-medium">Zatiaľ žiadni klienti</div>
          <div className="text-sm text-slate-400 mt-1">Pridajte prvého klienta tlačidlom „Pridať klienta".</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {companies.map((c) => (
            <CompanyRow
              key={c.id}
              company={c}
              stores={storesFor(c.id)}
              expanded={expandedId === c.id}
              onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              onDeleteCompany={deleteCompany}
              onDeleteStore={deleteStore}
              onImportDone={afterImport}
              onPlan={onPlan}
            />
          ))}
        </div>
      )}
    </div>
  );
}
